import { Box, Icon, Popover } from '@bechtel/gds';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { noop } from 'lodash';
import { FC, ReactNode, useRef, useState } from 'react';
import { IconButtonStyled, MultiLingualText } from '~/components';

interface CreateActionProps {
  canAddItem: boolean;
  addHandler?: () => void;
}

interface CloneActionProps {
  enableClone: boolean;
  cloneHandler?: () => void;
}

interface DeleteActionProps {
  enableDelete: boolean;
  deleteHandler: () => void;
}

interface CustomActionProps {
  enableAction: boolean;
  actionHandler: () => void;
  text: string;
  icon?: IconName;
  iconComponent?: ReactNode;
}

export const CreateAction: FC<CreateActionProps> = ({ canAddItem, addHandler = noop }) => {
  const addNewRef = useRef(null);
  const [showAddNewToolTipMessage, setShowAddNewToolTipMessage] = useState(false);
  return (
    <>
      <div ref={addNewRef}>
        <Box
          onMouseOver={() => canAddItem && setShowAddNewToolTipMessage(true)}
          onMouseLeave={() => setShowAddNewToolTipMessage(false)}
          onClick={(event) => (!canAddItem ? event.preventDefault() : addHandler())}
        >
          <IconButtonStyled disabled={!canAddItem}>
            <Icon icon="plus" iconStyle="regular" fillColor="primary.900" />
          </IconButtonStyled>
        </Box>
      </div>
      <Popover
        triggerComponentRef={addNewRef}
        showPopper={showAddNewToolTipMessage}
        customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 2 }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            Add New
          </MultiLingualText>
        </Box>
      </Popover>
    </>
  );
};

export const CloneAction: FC<CloneActionProps> = ({ enableClone, cloneHandler = noop }) => {
  const cloneRef = useRef(null);
  const [showCloneToolTipMessage, setShowCloneToolTipMessage] = useState(false);
  return (
    <>
      <div ref={cloneRef}>
        <Box
          onMouseOver={() => enableClone && setShowCloneToolTipMessage(true)}
          onMouseLeave={() => setShowCloneToolTipMessage(false)}
          onClick={(event) => (!enableClone ? event.preventDefault() : cloneHandler())}
        >
          <IconButtonStyled disabled={!enableClone}>
            <Icon icon="copy" iconStyle="regular" fillColor="primary.900" />
          </IconButtonStyled>
        </Box>
      </div>
      <Popover
        triggerComponentRef={cloneRef}
        showPopper={showCloneToolTipMessage}
        customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 1 }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            Clone
          </MultiLingualText>
        </Box>
      </Popover>
    </>
  );
};

export const DeleteAction: FC<DeleteActionProps> = ({ enableDelete, deleteHandler = noop }) => {
  const deleteRef = useRef(null);
  const [showDeleteToolTipMessage, setShowDeleteToolTipMessage] = useState(false);
  return (
    <>
      <div ref={deleteRef}>
        <Box
          onMouseOver={() => enableDelete && setShowDeleteToolTipMessage(true)}
          onMouseLeave={() => setShowDeleteToolTipMessage(false)}
          onClick={(event) => (!enableDelete ? event.preventDefault() : deleteHandler())}
        >
          <IconButtonStyled disabled={!enableDelete}>
            <Icon icon="trash-can" iconStyle="regular" fillColor="primary.900" />
          </IconButtonStyled>
        </Box>
      </div>
      <Popover
        triggerComponentRef={deleteRef}
        showPopper={showDeleteToolTipMessage}
        customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 1 }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            Delete
          </MultiLingualText>
        </Box>
      </Popover>
    </>
  );
};

export const CustomAction: FC<CustomActionProps> = ({
  enableAction,
  actionHandler = noop,
  text,
  icon,
  iconComponent,
}) => {
  const deleteRef = useRef(null);
  const [showActionToolTipMessage, setShowActionToolTipMessage] = useState(false);
  return (
    <>
      <div ref={deleteRef}>
        <Box
          onMouseOver={() => enableAction && setShowActionToolTipMessage(true)}
          onMouseLeave={() => setShowActionToolTipMessage(false)}
          onClick={(event) => (!enableAction ? event.preventDefault() : actionHandler())}
        >
          <IconButtonStyled disabled={!enableAction}>
            {iconComponent ? (
              <Box>{iconComponent}</Box>
            ) : (
              icon && <Icon icon={icon} iconStyle="regular" fillColor="primary.900" />
            )}
          </IconButtonStyled>
        </Box>
      </div>
      <Popover
        triggerComponentRef={deleteRef}
        showPopper={showActionToolTipMessage}
        customStyle={{ backgroundColor: 'black', color: 'white', zIndex: 1 }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            {text}
          </MultiLingualText>
        </Box>
      </Popover>
    </>
  );
};
